import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Calculator, BarChart3, MessageCircle } from "lucide-react";
import { API } from "@/App";
import { waLink } from "@/data/content";

const fmt = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

function SipLumpsum() {
  const [mode, setMode] = useState("sip");
  const [amount, setAmount] = useState(5000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);

  const result = useMemo(() => {
    if (mode === "sip") {
      const r = rate / 100 / 12;
      const n = years * 12;
      const value = r ? amount * (((1 + r) ** n - 1) / r) * (1 + r) : amount * n;
      return { invested: amount * n, value };
    }
    const value = amount * (1 + rate / 100) ** years;
    return { invested: amount, value };
  }, [mode, amount, years, rate]);

  return (
    <article className="tool-card sip-card" data-testid="sip-lumpsum-calculator">
      <div className="tool-head">
        <div><span className="tool-label">01 / WEALTH CALCULATOR</span><h3>SIP ya Lumpsum — dono calculate karein</h3></div>
        <Calculator size={24} />
      </div>
      <div className="mode-toggle" data-testid="calc-mode-toggle">
        <button className={mode === "sip" ? "active" : ""} onClick={() => setMode("sip")} data-testid="calc-mode-sip">SIP (Monthly)</button>
        <button className={mode === "lumpsum" ? "active" : ""} onClick={() => setMode("lumpsum")} data-testid="calc-mode-lumpsum">Lumpsum (One-time)</button>
      </div>
      <div className="slider-field">
        <div><label htmlFor="calc-amount">{mode === "sip" ? "Monthly investment" : "One-time investment"}</label><strong>{fmt(amount)}</strong></div>
        <input id="calc-amount" data-testid="calc-amount-input" type="range" min={mode === "sip" ? 500 : 5000} max={mode === "sip" ? 100000 : 1000000} step={mode === "sip" ? 500 : 5000} value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
      </div>
      <div className="slider-field">
        <div><label htmlFor="calc-years">Investment period</label><strong>{years} years</strong></div>
        <input id="calc-years" data-testid="calc-years-input" type="range" min="1" max="30" value={years} onChange={(e) => setYears(Number(e.target.value))} />
      </div>
      <div className="slider-field">
        <div><label htmlFor="calc-rate">Expected annual return</label><strong>{rate}%</strong></div>
        <input id="calc-rate" data-testid="calc-rate-input" type="range" min="6" max="18" value={rate} onChange={(e) => setRate(Number(e.target.value))} />
      </div>
      <div className="result-box">
        <span>Estimated value after {years} years</span>
        <strong data-testid="calc-result">{fmt(result.value)}</strong>
        <div><span>Invested {fmt(result.invested)}</span><span className="positive">Growth +{fmt(result.value - result.invested)}</span></div>
      </div>
      <a className="btn btn-whatsapp btn-block" href={waLink(`Namaste Utkarsh ji, maine calculator se dekha: ${mode === "sip" ? `₹${amount}/month SIP` : `₹${amount} lumpsum`} for ${years} years @${rate}%. Mujhe ispar salah chahiye.`)} target="_blank" rel="noopener noreferrer" data-testid="calc-whatsapp-button">
        <MessageCircle size={16} /> Is plan par baat karein
      </a>
      <small className="disclaimer">Illustration only. Returns are not guaranteed and market-linked investments carry risk.</small>
    </article>
  );
}

function FundExplorer() {
  const [mode, setMode] = useState("sip");
  const [category, setCategory] = useState("All");
  const [data, setData] = useState({ funds: [], source: "AMFI NAV history", loading: true });

  useEffect(() => {
    axios.get(`${API}/performance`)
      .then(({ data: res }) => setData({ funds: res.funds || [], source: res.source, loading: false }))
      .catch(() => setData({ funds: [], source: "AMFI data temporarily unavailable", loading: false }));
  }, []);

  const categories = ["All", ...new Set(data.funds.map((f) => f.category))];
  const funds = category === "All" ? data.funds : data.funds.filter((f) => f.category === category);
  const get = (fund) => (mode === "sip" ? fund.sip : fund.lumpsum) || {};

  return (
    <article className="tool-card performance-card" data-testid="performance-explorer">
      <div className="tool-head">
        <div><span className="tool-label">02 / FUND EXPLORER</span><h3>Live past performance — SIP & Lumpsum</h3></div>
        <BarChart3 size={24} />
      </div>
      <div className="mode-toggle light" data-testid="perf-mode-toggle">
        <button className={mode === "sip" ? "active" : ""} onClick={() => setMode("sip")} data-testid="perf-mode-sip">SIP Returns</button>
        <button className={mode === "lumpsum" ? "active" : ""} onClick={() => setMode("lumpsum")} data-testid="perf-mode-lumpsum">Lumpsum Returns</button>
      </div>
      <div className="filter-row">
        {categories.map((cat) => (
          <button key={cat} className={category === cat ? "filter active" : "filter"} onClick={() => setCategory(cat)} data-testid={`fund-filter-${cat.toLowerCase().replaceAll(" ", "-")}`}>{cat}</button>
        ))}
      </div>
      <div className="fund-table" data-testid="fund-performance-table">
        <div className="fund-table-head"><span>Fund</span><span>1Y</span><span>3Y</span><span>5Y</span></div>
        {data.loading && <div className="fund-loading" data-testid="fund-loading">Live AMFI data load ho raha hai…</div>}
        {!data.loading && funds.map((fund) => (
          <motion.div className="fund-table-row" key={fund.code || fund.name} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid={`fund-row-${(fund.category || "").toLowerCase().replaceAll(" ", "-")}`}>
            <div className="fund-name"><strong>{fund.name}</strong><span>{fund.category}{fund.nav ? ` • NAV ₹${fund.nav}` : ""}</span></div>
            {["1y", "3y", "5y"].map((k) => (
              <span key={k} className={(get(fund)[k] ?? 0) >= 0 ? "ret pos" : "ret neg"} data-testid={`fund-${k}-${fund.code}`}>
                {get(fund)[k] !== undefined ? `${get(fund)[k]}%` : "—"}
              </span>
            ))}
          </motion.div>
        ))}
      </div>
      <small className="disclaimer">Source: {data.source}. Returns are annualised, as of each fund's latest NAV. Past performance is not indicative of future results.</small>
    </article>
  );
}

export default function Tools() {
  return (
    <section className="section tools-section" id="tools" data-testid="tools-section">
      <div className="section-head centered">
        <div className="eyebrow green">Plan with confidence</div>
        <h2>Smart tools for <span className="accent">smarter choices.</span></h2>
        <p>SIP ya Lumpsum — pehle khud calculate karein, phir expert salah lein. Fund performance ka data live AMFI records se aata hai.</p>
      </div>
      <div className="tool-grid"><SipLumpsum /><FundExplorer /></div>
    </section>
  );
}
